import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { filterOrderByTableKds, filterOrderKds, getPendingOrder, updateStatus, OrderDetailsPrintPDF } from "./POSActions.js";
import { connect } from "react-redux";
import { socket } from "../../../../socket.js";
import { GlobalSpinner } from "../../../../global/globalFunctions";
import { isEmpty } from "lodash";
import audio from "../../../../Images/beep.mp3";
import moment from "moment";
import SinglePageDashbord from "../../drawer/SinglePageDashbord.jsx";



const ToastContainer = ({ toasts, onRemove }) => (
    <div style={{
        position: "fixed", top: "16px", right: "16px",
        zIndex: 9999, display: "flex", flexDirection: "column", gap: "8px",
        pointerEvents: "none",
    }}>
        {toasts.map((t) => (
            <div key={t.id} style={{ display: "flex", flexDirection: "column", animation: "kdsSlideIn .28s cubic-bezier(.16,1,.3,1) both", pointerEvents: "all" }}>
                <div style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "10px 14px 10px 10px", borderRadius: "40px",
                    background: "#fff",
                    border: `1.5px solid ${t.type === "success" ? "#86efac" : "#fca5a5"}`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "13px", fontWeight: 600,
                    color: t.type === "success" ? "#15803d" : "#b91c1c",
                    minWidth: "220px",
                }}>
                    <div style={{
                        width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
                        background: t.type === "success" ? "#dcfce7" : "#fee2e2",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        {t.type === "success"
                            ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        }
                    </div>
                    <span style={{ flex: 1 }}>{t.message}</span>
                    <span onClick={() => onRemove(t.id)} style={{ opacity: 0.4, cursor: "pointer", fontSize: "14px", marginLeft: "4px", lineHeight: 1 }}>✕</span>
                </div>
                <div style={{
                    height: "3px", marginLeft: "44px", borderRadius: "0 0 40px 40px",
                    background: t.type === "success" ? "#22c55e" : "#ef4444",
                    animation: "kdsProgress 3s linear forwards",
                }} />
            </div>
        ))}
    </div>
);
let _toastId = 0;
const useToast = () => {
    const [toasts, setToasts] = useState([]);
    const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
    const addToast = (type, message) => {
        const id = ++_toastId;
        setToasts((prev) => [...prev, { id, type, message }]);
        setTimeout(() => removeToast(id), 3200);
    };
    const showSuccess = (msg = "Order status updated successfully") => addToast("success", msg);
    const showError = (msg = "Something went wrong!") => addToast("error", msg);
    return { toasts, removeToast, showSuccess, showError };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getElapsedMinutes = (createdAt) => Math.floor(moment.duration(moment().diff(moment(createdAt))).asMinutes());
const getTimerStyle = (minutes) => {
    if (minutes <= 10) return { bg: "#dcfce7", color: "#15803d" };
    if (minutes <= 25) return { bg: "#fef3c7", color: "#b45309" };
    return { bg: "#fee2e2", color: "#b91c1c" };
};
const STATUS_STYLES = {
    Pending: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    Cooking: { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" },
    Served: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
};
const TOP_BAR_COLORS = {
    Pending: "#3b82f6",
    Cooking: "#f59e0b",
    Served: "#22c55e",
};

// ─── SVG Icons ────────────────────────────────────────────────────────────────

const ClockIcon = ({ size = 12, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const PrinterIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
    </svg>
);
const SearchIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b92a9" strokeWidth="2" strokeLinecap="round">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
);

// ─── Stats Bar ────────────────────────────────────────────────────────────────

const StatsBar = ({ orders }) => {
    const total = orders.length;
    const pending = orders.filter(o => o.status === "Pending").length;
    const cooking = orders.filter(o => o.status === "Cooking").length;
    const late = orders.filter(o => getElapsedMinutes(o.createdAt) > 25 && o.status !== "Served").length;

    const stats = [
        { label: "Total Today", value: total, color: "#1a1d2e" },
        { label: "Pending", value: pending, color: "#2563eb" },
        { label: "Cooking", value: cooking, color: "#d97706" },
        { label: "Late (>25min)", value: late, color: "#dc2626" },
    ];

    return (
        <div style={S.statsBar}>
            {stats.map((s, i) => (
                <div key={i} style={S.statCard}>
                    <div style={{ fontSize: "22px", fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#8b92a9", fontWeight: 500, marginTop: "3px" }}>{s.label}</div>
                </div>
            ))}
        </div>
    );
};


const OrderCard = ({ item, onAccept, onReady, onPrint }) => {
    const elapsed = getElapsedMinutes(item.createdAt);
    const timerStyle = getTimerStyle(elapsed);
    const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.Pending;
    const topBarColor = TOP_BAR_COLORS[item.status] || "#3b82f6";
    const tableName = !isEmpty(item.tableId) ? item.tableId.tableName : null;

    return (
        <div style={S.card}>
            <div style={{ height: "4px", background: topBarColor }} />

            <div style={S.cardHead}>
                <div style={S.row}>
                    <span style={S.orderNum}>#{item.number}</span>
                    <span style={{ ...S.timer, background: timerStyle.bg, color: timerStyle.color }}>
                        <ClockIcon color={timerStyle.color} />
                        {elapsed} min
                    </span>
                </div>
                <span style={{ ...S.statusPill, background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
                    {item.status}
                </span>
            </div>

            <div style={S.customerRow}>
                <span style={S.customerName}>{item.customer?.name || "Guest"}</span>
                {tableName && <span style={S.tableBadge}>{tableName}</span>}
            </div>

            <div style={S.itemsList}>
                {item.items.map((item1, i1) => {
                    const isDimmed = item.isEdit === true && item1.itemStatus === "Pending";
                    const isDeal = item1.product?.productTypeName === "Deal";

                    if (isDeal) {
                        return (
                            <div key={i1} style={{ ...S.itemRow, flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                    <span style={{ ...S.itemName, color: "#7c3aed", fontWeight: 700 }}>
                                        🎁 {item1.productLabel}
                                    </span>
                                    <span style={{ ...S.itemQty, background: "#f3f0ff", color: "#7c3aed" }}>{item1.quantity}</span>
                                </div>
                                <div style={{ fontSize: "11px", color: "#8b92a9", paddingLeft: "4px", lineHeight: 1.7 }}>
                                    {item1.product.variation?.map((v, vi) => (
                                        <div key={vi}>• {v.ProductLabel} — {v.ProductVariationLabel} <b>×{v.quantity}</b></div>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    const variation = item1.productLabel !== item1.productVariationLabel
                        ? ` — ${item1.productVariationLabel}` : "";

                    return (
                        <div key={i1} style={{ ...S.itemRow, ...(isDimmed ? S.dimmedRow : {}) }}>
                            <div style={{ flex: 1 }}>
                                <span style={S.itemName}>
                                    {item1.productLabel}
                                    {variation && <span style={S.itemVariation}>{variation}</span>}
                                </span>
                                {item1.comment && <span style={S.itemComment}>{item1.comment}</span>}
                            </div>
                            <span style={S.itemQty}>{item1.quantity}</span>
                        </div>
                    );
                })}
            </div>

            <div style={S.cardFooter}>
                <button
                    disabled={item.status !== "Pending"}
                    className="kds-btn-a"
                    style={{ ...S.btnAccept, ...(item.status !== "Pending" ? S.btnDisabled : {}) }}
                    onClick={() => onAccept(item)}
                >
                    ✓ Accept
                </button>
                <button
                    disabled={item.status !== "Cooking"}
                    className="kds-btn-r"
                    style={{ ...S.btnReady, ...(item.status !== "Cooking" ? S.btnDisabled : {}) }}
                    onClick={() => onReady(item)}
                >
                    ✓ Ready
                </button>
                <button className="kds-btn-p" style={S.btnPrint} onClick={() => onPrint(item._id)}>
                    <PrinterIcon />
                </button>
            </div>
        </div>
    );
};


function KDSView(props) {
    const [search, setSearch] = useState("");
    const [searchTable, setSearchTable] = useState("");
    const playAudio = () => new Audio(audio).play();
    const { toasts, removeToast, showSuccess, showError } = useToast();
    const handleVisible = () => showSuccess();
    const handleDangerVisible = () => showError();

    useEffect(() => {
        props.getPendingOrder();
        socket.on("addOrderSocket", () => { props.getPendingOrder(); playAudio(); });
        socket.on("cancelOrderSocket", () => { props.getPendingOrder(); playAudio(); });
        // socket.on("timeUpdate", () => { props.getPendingOrder(); });
        socket.on("updateOrderSocket", () => { props.getPendingOrder(); });
        return () => {
            socket.off("addOrderSocket");
            socket.off("cancelOrderSocket");
            // socket.off("timeUpdate");
            socket.off("updateOrderSocket");
        };
    }, []);

    const handleAccept = (item) => {
        props.updateStatus(item, "Cooking", "", "", handleVisible, handleDangerVisible);
        props.getPendingOrder();
    };

    const handleReady = (item) => {
        props.updateStatus(item, "Served", "", "", handleVisible, handleDangerVisible);
        props.getPendingOrder();
    };

    const orders = props.SearchPendingOrderList || [];
    const allOrders = props.PendingOrderList || [];

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        .kds-si::placeholder { color: #b0b7cc; }
        .kds-si:focus { border-color: #3b82f6 !important; outline: none; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
        .kds-card:hover { box-shadow: 0 6px 24px rgba(59,130,246,0.10) !important; transform: translateY(-2px); transition: transform .18s, box-shadow .18s; }
        .kds-btn-a:hover:not(:disabled) { background: #1d4ed8 !important; }
        .kds-btn-r:hover:not(:disabled) { background: #15803d !important; }
        .kds-btn-p:hover { background: #e2e5ee !important; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #e2e5ee; border-radius: 4px; }
        @keyframes kdsSlideIn { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes kdsProgress { from { width: 100%; } to { width: 0%; } }
      `}</style>

            <ToastContainer toasts={toasts} onRemove={removeToast} />

            <div style={S.root}>
                {/* Navbar */}
                <SinglePageDashbord header='Kitchen Display System' />

                {/* Stats */}
                <StatsBar orders={allOrders} />

                {/* Body */}
                {props.isGettingPendingOrder ? (
                    <GlobalSpinner />
                ) : (
                    <div style={S.body}>
                        <div style={S.searchRow}>
                            <div style={S.searchWrap}>
                                <SearchIcon />
                                <input
                                    className="kds-si"
                                    style={S.searchInput}
                                    type="text"
                                    placeholder="Search Order No."
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setSearchTable("");
                                        props.searchOrder(e.target.value);
                                    }}
                                />
                            </div>
                            {/* <div style={S.searchWrap}>
                                <SearchIcon />
                                <input
                                    className="kds-si"
                                    style={S.searchInput}
                                    type="text"
                                    placeholder="Search by table..."
                                    value={searchTable}
                                    onChange={(e) => {
                                        setSearchTable(e.target.value);
                                        setSearch("");
                                        props.searchOrderbyTable(e.target.value);
                                    }}
                                />
                            </div> */}
                        </div>

                        {isEmpty(orders) ? (
                            <div style={S.emptyState}>
                                <div style={{ fontSize: "44px", marginBottom: "12px" }}>🍽️</div>
                                <div style={{ fontWeight: 700, fontSize: "16px", color: "#374151" }}>No orders found</div>
                                <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>All caught up!</div>
                            </div>
                        ) : (
                            <Row>
                                {orders.map((item, i) => (
                                    <Col key={i} xl={3} lg={4} md={6} sm={12} style={{ marginBottom: "4px" }}>
                                        <div className="kds-card">
                                            <OrderCard
                                                item={item}
                                                onAccept={handleAccept}
                                                onReady={handleReady}
                                                onPrint={(id) => props.OrderDetailsPrintPDF(id)}
                                            />
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}


const S = {
    root: { minHeight: "100vh", backgroundColor: "#f5f6fa", fontFamily: "'Plus Jakarta Sans', sans-serif" },
    navbar: { backgroundColor: "#fff", borderBottom: "1.5px solid #e8ebf4", padding: "0 24px", height: "62px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 },
    navLeft: { display: "flex", alignItems: "center", gap: "12px" },
    logoBox: { width: "38px", height: "38px", background: "#2563eb", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    brand: { fontSize: "15px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-.01em" },
    brandSub: { fontSize: "10px", color: "#8b92a9", fontWeight: 600, letterSpacing: ".06em" },
    activePill: { background: "#eef2ff", color: "#3b5bdb", fontSize: "12px", fontWeight: 700, borderRadius: "20px", padding: "4px 12px" },
    statsBar: { background: "#fff", borderBottom: "1px solid #e8ebf4", padding: "10px 24px", display: "flex", gap: "12px" },
    statCard: { flex: 1, background: "#f8f9fc", border: "1px solid #eaecf5", borderRadius: "10px", padding: "9px 14px" },
    body: { padding: "20px 24px" },
    searchRow: { display: "flex", gap: "12px", marginBottom: "20px" },
    searchWrap: { flex: 1, display: "flex", alignItems: "center", background: "#fff", border: "1.5px solid #e2e5ee", borderRadius: "10px", padding: "0 14px", gap: "8px" },
    searchInput: { flex: 1, border: "none", background: "transparent", color: "#1a1d2e", padding: "9px 0", fontSize: "13px", fontFamily: "'Plus Jakarta Sans', sans-serif" },
    card: { background: "#fff", borderRadius: "14px", border: "1.5px solid #e8ebf4", overflow: "hidden", marginBottom: "16px" },
    cardHead: { padding: "12px 14px 10px", borderBottom: "1px solid #f0f2f8", display: "flex", alignItems: "center", justifyContent: "space-between" },
    row: { display: "flex", alignItems: "center", gap: "8px" },
    orderNum: { background: "#f0f2f8", color: "#1a1d2e", fontWeight: 800, fontSize: "15px", borderRadius: "8px", padding: "3px 10px" },
    timer: { borderRadius: "7px", padding: "3px 10px", fontWeight: 700, fontSize: "11px", display: "flex", alignItems: "center", gap: "4px" },
    statusPill: { borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: 700, letterSpacing: ".03em" },
    customerRow: { padding: "8px 14px 10px", borderBottom: "1px solid #f0f2f8", display: "flex", justifyContent: "space-between", alignItems: "center" },
    customerName: { color: "#1a1d2e", fontWeight: 700, fontSize: "13px" },
    tableBadge: { background: "#f0f2f8", color: "#5c6380", fontSize: "11px", fontWeight: 600, borderRadius: "6px", padding: "2px 8px" },
    itemsList: { padding: "10px 12px", display: "flex", flexDirection: "column", gap: "5px" },
    itemRow: { background: "#f8f9fc", border: "1px solid #eaecf5", borderRadius: "8px", padding: "7px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    dimmedRow: { opacity: 0.5, background: "#f5f5f5" },
    itemName: { color: "#374151", fontSize: "12.5px", fontWeight: 500 },
    itemVariation: { color: "#8b92a9" },
    itemComment: { color: "#9ca3af", fontSize: "11px", display: "block", marginTop: "2px" },
    itemQty: { background: "#eef2ff", color: "#3b5bdb", fontWeight: 800, fontSize: "12px", borderRadius: "6px", padding: "1px 8px", minWidth: "26px", textAlign: "center" },
    cardFooter: { padding: "10px 14px", borderTop: "1px solid #f0f2f8", display: "flex", gap: "7px", alignItems: "center", background: "#fafbff" },
    btnAccept: { background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "background .15s" },
    btnReady: { background: "#16a34a", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "background .15s" },
    btnPrint: { background: "#f0f2f8", color: "#5c6380", border: "none", borderRadius: "8px", padding: "6px 10px", fontSize: "12px", cursor: "pointer", marginLeft: "auto", display: "flex", alignItems: "center", fontFamily: "'Plus Jakarta Sans', sans-serif", transition: "background .15s" },
    btnDisabled: { opacity: 0.35, cursor: "not-allowed" },
    loaderWrap: { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "70vh", gap: "14px" },
    emptyState: { textAlign: "center", padding: "80px 0", width: "100%" },
};

const mapDispatchToProps = (dispatch) => ({
    getPendingOrder: () => dispatch(getPendingOrder()),
    updateStatus: (data, status, a, b, ok, err) => dispatch(updateStatus(data, status, a, b, ok, err)),
    OrderDetailsPrintPDF: (id) => dispatch(OrderDetailsPrintPDF(id)),
    searchOrder: (data) => dispatch(filterOrderKds(data)),
    searchOrderbyTable: (data) => dispatch(filterOrderByTableKds(data)),
});

const mapStateToProps = (state) => ({
    PendingOrderList: state.POSReducer.PendingOrderList,
    isGettingPendingOrder: state.POSReducer.isGettingPendingOrder,
    SearchPendingOrderList: state.POSReducer.SearchPendingOrderList,
});

export default connect(mapStateToProps, mapDispatchToProps)(KDSView);